/**
 * scrollWithin.ts
 *
 * Scroll helpers scoped to a single container.
 *
 * `Element.scrollIntoView()` scrolls EVERY scrollable ancestor on BOTH axes, and an
 * `overflow: hidden` box is still programmatically scrollable. In the Comm Center the
 * conversation sits inside a clipped flex row whose left 320px is the inbox, and below
 * `md` the shell releases the viewport lock so the document itself is the vertical
 * scroller — so one call on the thread's bottom sentinel dragged the whole page down
 * into the middle of the message list and sideways past the inbox (SCRUM-1362).
 *
 * These helpers move one known scrollport and nothing else. The visible outcome inside
 * that scrollport is the same as `scrollIntoView` produced; only the blast radius changes.
 */

/**
 * Scroll `container` to an absolute offset, clamped at the top.
 *
 * Feature-detects `scrollTo`: jsdom implements `scrollTop` but not
 * `Element.prototype.scrollTo`, so the smooth path would throw under test.
 */
export function scrollContainerTo(container: HTMLElement, top: number, smooth = false): void {
  const clamped = Math.max(0, top);
  if (smooth && typeof container.scrollTo === 'function') {
    container.scrollTo({ top: clamped, behavior: 'smooth' });
    return;
  }
  container.scrollTop = clamped;
}

/** Pin `container` to its newest content. Over-scroll is clamped by the browser. */
export function scrollToBottomOf(container: HTMLElement, smooth = false): void {
  scrollContainerTo(container, container.scrollHeight, smooth);
}

/**
 * Centre `el` within `container`'s scrollport.
 *
 * Rect-based on purpose. `offsetTop` is measured from the nearest *positioned* ancestor,
 * and neither the thread's scroll container nor a message row is positioned — their
 * offsetParent is the page shell — so offset arithmetic would silently target the wrong
 * box. Rects are relative to the viewport for both, so their difference is exact.
 */
export function scrollElementIntoCenter(
  container: HTMLElement,
  el: HTMLElement,
  smooth = true,
): void {
  const containerRect = container.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const delta = (elRect.top - containerRect.top) - (container.clientHeight - elRect.height) / 2;
  scrollContainerTo(container, container.scrollTop + delta, smooth);
}

/**
 * Bring `el` into `container`'s horizontal scrollport, moving the minimum distance —
 * the same visible outcome as `scrollIntoView({ inline: 'nearest' })`, scoped to one box.
 *
 * This is the axis that caused the damage. `inline: 'nearest'` on a tab strip walked up
 * and scrolled every ancestor horizontally, including `overflow-hidden` shells (still
 * programmatically scrollable) and, below `md`, the document — so tapping a section tab
 * slid the whole chart sideways and the patient header, id, Details button and tab row
 * all read as cut off. Reported in nine tickets before the cause was found.
 *
 * Rect-based for the reason given above `scrollElementIntoCenter`: neither a strip nor
 * its items are positioned, so `offsetLeft` would measure against the page shell.
 *
 * No-ops when the container does not scroll, which is every width where the strip fits.
 */
export function scrollElementIntoViewX(
  container: HTMLElement,
  el: HTMLElement,
  smooth = false,
): void {
  if (container.scrollWidth <= container.clientWidth) return;

  const containerRect = container.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();

  const overflowLeft = elRect.left - containerRect.left;
  const overflowRight = elRect.right - containerRect.right;

  // Already fully visible — do nothing rather than re-centre, so an item the user can
  // see does not jump under their finger.
  if (overflowLeft >= 0 && overflowRight <= 0) return;

  const delta = overflowLeft < 0 ? overflowLeft : overflowRight;
  const left = Math.max(0, container.scrollLeft + delta);

  if (smooth && typeof container.scrollTo === 'function') {
    container.scrollTo({ left, behavior: 'smooth' });
    return;
  }
  container.scrollLeft = left;
}

/**
 * Reveal `el` inside its NEAREST horizontally-scrolling ancestor, and stop there.
 *
 * For a control that does not own its own scrollport — a pill rendered by one component
 * into a row owned by another, where passing a ref down would couple the two. The walk
 * is what makes this safe: it stops at the first box that actually scrolls, where
 * `scrollIntoView` would carry on and move every scroller above it as well, including
 * the document.
 *
 * Returns the container it moved, or null if nothing above `el` scrolls horizontally.
 */
export function scrollAncestorIntoViewX(el: HTMLElement, smooth = false): HTMLElement | null {
  let node = el.parentElement;

  while (node && node !== document.body) {
    if (node.scrollWidth > node.clientWidth) {
      const overflowX = getComputedStyle(node).overflowX;
      if (overflowX === 'auto' || overflowX === 'scroll') {
        scrollElementIntoViewX(node, el, smooth);
        return node;
      }
    }
    node = node.parentElement;
  }

  return null;
}
